import React, { InputHTMLAttributes, ReactNode } from 'react'
import {
  RadioButtonInternalProps,
  ContentRadio,
  StyledRadioButtonIcon,
  ContentRadioProps
} from 'library/atoms/RadioButton'
import styled from '@emotion/styled'
import { CoreTheme } from 'assets/theme/theme'
import { useField } from 'shared/providers/FormProvider/formProvider'

interface TimeSlotProps
  extends Pick<InputHTMLAttributes<HTMLInputElement>, 'disabled'>,
    Pick<ContentRadioProps, 'onClick'> {
  children?: ReactNode
  name: string
  value: string
  className?: string
  variant?: 'membership'
  gaid?: string
}

/**
 * Combine drop-shadows to create the border.
 * Not using "border" because this element use the child element to create the effect of clip-mask.
 * */
const generateBorder = ({
  theme,
  checked,
  focused
}: TimeSlotElementsProps & { theme: CoreTheme; focused?: boolean }) => {
  const BORDER_WIDTH = 1
  const COLORS = {
    primary: focused ? theme.colors.primary : theme.colors.border.primary,
    secondary: focused ? theme.colors.border.primary : theme.colors.primary
  }
  const COLOR = checked ? COLORS.secondary : COLORS.primary

  return `drop-shadow(${BORDER_WIDTH}px 0px 0px ${COLOR}) drop-shadow(-${BORDER_WIDTH}px 0px 0px ${COLOR}) drop-shadow(0px ${BORDER_WIDTH}px 0px ${COLOR}) drop-shadow(0px -${BORDER_WIDTH}px 0px ${COLOR})`
}

type StyledLabelProps = Pick<RadioButtonInternalProps, 'checked'> &
  Pick<TimeSlotProps, 'variant'>

/**
 * Time Slot is used in place of a traditional `<input type="radio" />` element. This has
 * Formik baked into it for ease of use.
 *
 * We are not using the Radio Button component to avoid using duplicate labels.
 *
 * It uses styled-system to add margins if necessary, e.g. a section of
 * two inputs in the same row and the second input needs margin-let.
 */
const TimeSlot = ({
  name,
  value,
  children,
  variant,
  onClick,
  gaid,
  ...other
}: TimeSlotProps) => {
  const { field } = useField<string | undefined>(name)

  const checked = field.value === value

  return (
    <StyledTimeSlot
      name={name}
      value={value}
      variant={variant}
      checked={checked}
      onClick={value => {
        onClick && onClick(value)
      }}
      data-gaid={gaid}
      {...other}
    >
      {children && (
        <StyledLabel variant={variant} checked={checked}>
          {children}
        </StyledLabel>
      )}
      <TicketBackground checked={checked} />
    </StyledTimeSlot>
  )
}

export default TimeSlot

type TimeSlotElementsProps = RadioButtonInternalProps

/**
 * The TimeSlot element has a complex layout that could be done with `clip-path` or only pseud elements
 * but those options have been discarded because this element also demands dropshadow animation and be able
 * to expand without stretching.
 *
 * Breaking the background into small shapes allow the element to expand without affecting the size of the rounded
 * elements on the side.
 */
const TicketBackground = ({ checked }: TimeSlotElementsProps) => (
  <StyledTicketBackground checked={checked}>
    <TicketBackgroundSVGWrapper checked={checked}>
      <RoundedShapeSVG />
      <span />
      <RoundedShapeSVG />
    </TicketBackgroundSVGWrapper>
  </StyledTicketBackground>
)

const SVG_HEIGHT = 30

const RoundedShapeSVG = () => (
  <svg
    width="15"
    height={SVG_HEIGHT}
    viewBox="0 0 15 30"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15 0H0V3C6.62742 3 12 8.41591 12 15.0968C12 21.7776 6.62742 27.1936 0 27.1936V30H15V0Z"
      fill="currentColor"
    />
  </svg>
)

const generateColorForBackground = ({
  theme,
  checked,
  disabled
}: TimeSlotElementsProps & { theme: CoreTheme }) => {
  if (checked) return theme.colors.timeSlot.active
  else if (disabled) return theme.colors.timeSlot.disabled
  return theme.colors.timeSlot.neutral
}

/**
 * Use this element to wrap the content to create different columns inside TimeSlot.
 * */
export const TimeSlotContent = styled.div(({ theme }) => ({
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  ':first-of-type': {
    paddingRight: theme.space[2]
  },
  // Not using last-of-type because this component has other divs
  ':not(:first-of-type)': {
    alignItems: 'flex-end',
    textAlign: 'right',
    paddingLeft: theme.space[2]
  },
  // We need to have this selector for membership details, otherwise we will also target price
  ':nth-of-type(2):not(:last-of-type)': {
    textAlign: 'left',
    color: theme.colors.text.secondary,
    padding: theme.space[2]
  },
  '> * + *': {
    marginTop: theme.space[2]
  },
  // force checked elements to change text color to white
  '[aria-checked="true"] & > *': {
    color: theme.colors.white
  }
}))

const StyledTimeSlot = styled(ContentRadio)<
  RadioButtonInternalProps & TimeSlotProps
>(
  ({ theme, checked, disabled }) => ({
    position: 'relative',
    overflow: 'hidden',
    justifyContent: 'space-between', // the rest of flex values is provided by ContentRadio
    height: 120,
    padding: theme.mixins.pxSpread([theme.space[4], theme.space[5]]),
    color: checked ? theme.colors.white : theme.colors.text.primary,
    background: 'none', // overwrite ContentRadio style
    border: 'none', // overwrite ContentRadio style
    borderRadius: theme.radii[2],
    filter: generateBorder({ theme, checked }),
    pointerEvents: disabled ? 'none' : 'auto',
    ...theme.mixins.fadeIn,
    hr: {
      zIndex: 2
    },
    '&:focus-visible': {
      outline: 'none',
      filter: `${generateBorder({
        theme,
        checked,
        focused: true
      })}`
    },
    '&:hover': {
      border: 'none', // overwrite ContentRadio style
      boxShadow: 'none', // overwrite ContentRadio style
      filter: `${generateBorder({
        theme,
        checked
      })} drop-shadow(${theme.shadows.primary[1]})`
    },
    '[role="radio"] + &': {
      marginTop: theme.space[3]
    },

    [`${StyledRadioButtonIcon}`]: {
      display: disabled ? 'none' : 'block',
      zIndex: 3,
      padding: theme.space[1],
      marginRight: 0,
      marginLeft: theme.space[2],
      '[aria-checked="true"] &': {
        borderColor: theme.colors.white
      }
    }
  }),
  ({ variant }) =>
    variant === 'membership' && {
      height: 'auto'
    }
)

const StyledLabel = styled.label<StyledLabelProps>(
  () => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%'
  }),
  ({ theme, variant, checked }) =>
    variant === 'membership' && {
      height: 'auto',
      zIndex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      color: checked ? theme.colors.white : theme.colors.text.tertiary,
      '> div': {
        color: 'inherit',
        span: {
          color: 'inherit'
        }
      },
      h6: {
        '&:first-of-type': {
          color: checked ? theme.colors.white : theme.colors.text.primary
        }
      }
    }
)

const StyledTicketBackground = styled.div<TimeSlotElementsProps>(
  ({ theme, checked, disabled }) => ({
    pointerEvents: 'none',
    ...theme.mixins.positionAbsoluteFillContent(),
    zIndex: 0,
    display: 'flex',
    flexWrap: 'wrap',
    alignSelf: 'normal',
    '&::before, &::after': {
      content: '""',
      display: 'block',
      height: `calc(50% - ${SVG_HEIGHT / 2}px)`,
      width: '100%',
      background: generateColorForBackground({ theme, checked, disabled })
    }
  })
)

const TicketBackgroundSVGWrapper = styled.div<TimeSlotElementsProps>(
  ({ theme, checked, disabled }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignSelf: 'normal',
    color: generateColorForBackground({ theme, checked, disabled }), // for the SVG
    height: SVG_HEIGHT,
    width: '100%',
    svg: {
      ':last-of-type': {
        transform: 'rotate(-180deg)'
      }
    },
    span: {
      flex: '1 0 0',
      background: generateColorForBackground({ theme, checked, disabled })
    }
  })
)
